import React, { useState } from 'react';
import { Edit, Trash2, Check, Star, Target, StickyNote, Plus, ExternalLink, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { WishItem } from '@/utils/storage';
import EditWishListItem from './EditWishListItem';

interface WishListItemProps {
  item: WishItem;
  affordability: number; // Percentage of how much user can afford (0-100+)
  currency: string;
  currentBalance: number;
  editMode: boolean;
  viewMode: 'unachieved' | 'achieved';
  onEdit: (updates: Partial<WishItem>) => void;
  onDelete: () => void;
  onMarkAchieved: () => void;
  onUpdateProgress: (progress: number) => void;
  onReAdd?: () => void;
}

const WishListItem: React.FC<WishListItemProps> = ({
  item,
  affordability,
  currency,
  currentBalance,
  editMode,
  viewMode,
  onEdit,
  onDelete,
  onMarkAchieved,
  onUpdateProgress,
  onReAdd
}) => {
  const [showEditForm, setShowEditForm] = useState(false);


  const canAfford = affordability >= 100;
  const isAlmostAffordable = affordability >= 80 && affordability < 100;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };


  if (showEditForm) {
    return (
      <EditWishListItem
        item={item}
        onSave={(updates) => {
          onEdit(updates);
          setShowEditForm(false);
        }}
        onCancel={() => setShowEditForm(false)}
        walletEnabled={currency === '$'}
      />
    );
  }

  return (
    <Card className={cn(
      "transition-all duration-200",
      item.achieved && "opacity-75 bg-muted/50",
      canAfford && !item.achieved && "ring-2 ring-green-200 bg-green-50/50",
      isAlmostAffordable && !item.achieved && "ring-2 ring-yellow-200 bg-yellow-50/50"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={cn(
                "font-semibold text-lg",
                item.achieved && "line-through text-muted-foreground"
              )}>
                {item.title}
              </h3>
              
              {item.purchaseLink && (
                <a
                  href={item.purchaseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                >
                  <ExternalLink className="w-3 h-3" />
                  Buy
                </a>
              )}
              
              {item.achieved && (
                <Badge className="bg-green-500 text-white">
                  <Check className="w-3 h-3 mr-1" />
                  Achieved
                </Badge>
              )}
            </div>
            
            {item.description && (
              <p className="text-muted-foreground text-sm">
                {item.description}
              </p>
            )}
          </div>

          {editMode && (
            <div className="flex gap-1 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditForm(true)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Cost and Affordability */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Cost:</span>
              <span className="font-semibold">
                {item.cost} {currency}
              </span>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>Affordability:</span>
                <span className={cn(
                  "font-medium",
                  canAfford ? "text-green-600" : isAlmostAffordable ? "text-yellow-600" : "text-red-600"
                )}>
                  {Math.min(affordability, 999).toFixed(0)}%
                </span>
              </div>
              <Progress 
                value={Math.min(affordability, 100)} 
                className="h-2"
              />
              <div className="text-xs text-muted-foreground">
                {canAfford 
                  ? `You can afford this! (${currentBalance - item.cost} ${currency} left over)`
                  : `Need ${item.cost - currentBalance} more ${currency}`
                }
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Progress:</span>
              <span className="font-semibold">{item.progress}%</span>
            </div>
            <Progress value={item.progress} className="h-2" />
          </div>
        </div>

        {/* Additional Info */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Star className="h-4 w-4" />
            <span>Added: {formatDate(item.dateAdded)}</span>
          </div>
          
          {item.purchaseLink && (
            <a
              href={item.purchaseLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View Product
            </a>
          )}
        </div>

        {item.notes && (
          <div className="border-t pt-3">
            <div className="flex items-start gap-2">
              <StickyNote className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <span className="text-sm font-medium text-muted-foreground">Notes:</span>
                <p className="text-sm mt-1">{item.notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {viewMode === 'unachieved' && !item.achieved && (
          <div className="flex gap-2 pt-2">
            {canAfford && (
              <Button 
                onClick={onMarkAchieved}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Check className="mr-2 h-4 w-4" />
                Mark as Achieved
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={() => {
                const newProgress = Math.min(item.progress + 10, 100);
                onUpdateProgress(newProgress);
              }}
              disabled={item.progress >= 100}
            >
              <Target className="mr-2 h-4 w-4" />
              +10% Progress
            </Button>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                const shareText = `Check out this item on my wish list: ${item.title} (${item.cost} ${currency})${item.purchaseLink ? ` - ${item.purchaseLink}` : ''}`;
                if (navigator.share) {
                  navigator.share({ title: item.title, text: shareText });
                } else {
                  navigator.clipboard.writeText(shareText);
                }
              }}
            >
              <Share className="h-4 w-4" />
            </Button>
          </div>
        )}

        {viewMode === 'achieved' && item.achieved && (
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="h-4 w-4" />
              <span>Achieved on {formatDate(item.achievedDate || '')}</span>
            </div>
            {onReAdd && (
              <Button 
                variant="outline"
                size="sm"
                onClick={onReAdd}
              >
                <Plus className="mr-2 h-4 w-4" />
                Re-add
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WishListItem;